<template>
  <div class="filter">
    <div class="row">

      <div class="small-1 columns end">
        <div class="status">
          <label class="field-label">Work Sector:</label>
          <select v-model="selectedSector" :disabled="sectorLoading || loading">
            <option value="0">ALL</option>
            <option v-for="sector in sectors" :value="sector.id" :key="sector.id">{{ sector.name }}</option>
          </select>
        </div>
      </div>

      <div class="small-4 columns end">
        <div class="date-box row">
          <div class="small-6 columns end">
            <div class="status">
              <label class="field-label">Start Date/Time:</label>
              <datepicker :format="calendarFormat" class="datepicker inline custom-pa-availability" 
                :input-class="{'datepicker-empty': dateError.target === 'date'}"
                v-model="startDate.date" v-on:input="changeDate('start')" placeholder="MM/DD/YYYY" :disabled-picker="loading"></datepicker>
              <div class="time">
                <select v-model="startDate.hour" @change="updateDate('start')"
                  :disabled="loading" :class="{'has-error':dateError.target === 'time'}">
                  <option v-for="n in maxHour" :value="leadingZero(n)">{{ leadingZero(n) }}</option>
                </select>
                <select v-model="startDate.minute" @change="updateDate('start')"
                  :disabled="loading" :class="{'has-error':dateError.target === 'time'}">
                  <option value="00">00</option>
                  <option v-for="n in 11" :value="leadingZero(n * 5)">{{ leadingZero(n * 5) }}</option>
                </select>
                <select v-model="startDate.amPm" @change="updateDate('start')"
                  v-if="!isMilitaryTime" :disabled="loading"
                  :class="{'has-error':dateError.target === 'time'}">
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
          </div>

          <div class="small-6 columns end">
            <div class="status end-date">
              <label class="field-label">End Date/Time:</label>
              <datepicker :format="calendarFormat" class="datepicker inline custom-pa-availability"
                :input-class="{'datepicker-empty': dateError.target === 'date'}"
                v-model="endDate.date" v-on:input="changeDate('end')" placeholder="MM/DD/YYYY" :disabled-picker="loading"></datepicker>
              <div class="time">
                <select v-model="endDate.hour" @change="updateDate('end')"
                  :disabled="loading" :class="{'has-error':dateError.target === 'time'}">
                  <option v-for="n in maxHour" :value="leadingZero(n)">{{ leadingZero(n) }}</option>
                </select>
                <select v-model="endDate.minute" @change="updateDate('end')"
                  :disabled="loading" :class="{'has-error':dateError.target === 'time'}">
                  <option value="00">00</option>
                  <option v-for="n in 11" :value="leadingZero(n * 5)">{{ leadingZero(n * 5) }}</option>
                </select>
                <select v-model="endDate.amPm" @change="updateDate('end')"
                  v-if="!isMilitaryTime" :disabled="loading"
                  :class="{'has-error':dateError.target === 'time'}">
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
          </div>

          <transition name="slide-up">
            <div class="error-box" v-if="dateError.showMsg">Invalid Date Range.</div>
          </transition>
        </div>
      </div>

      <div class="small-6 columns end">
        <div class="row">
          <div class="small-6 medium-3 columns end">
            <div class="status">
              <label class="field-label">Shift Preset:</label>
              <div class="d-flex align-center">
                <div :class="['shift-btn', {active:shiftActive==='AM', disabled:sectorLoading || loading}]" @click="toggleShift('AM')">AM</div>
                <div class="shift-slash">/</div>
                <div :class="['shift-btn', {active:shiftActive==='PM', disabled:sectorLoading || loading}]" @click="toggleShift('PM')">PM</div>
              </div>
            </div>
          </div>

          <div class="small-6 medium-2 columns end">
            <div class="status">
              <label class="field-label">Type:</label>
              <select v-model="selectedType" :disabled="sectorLoading || loading">
                <option value="">ALL</option>
                <option value="tnc">TNC</option>
                <option value="tcp">TCP</option>
                <option value="psc">PSC</option>
              </select>
            </div>
          </div>

          <div class="small-6 medium-2 columns end">
            <div class="status">
              <label class="field-label">Vehicle Type:</label>
              <select v-model="vehicleType" :disabled="sectorLoading || loading">
                <option value="">ALL</option>
                <option value="Sedan">Sedan</option>
                <option value="Mini SUV">Mini SUV</option>
                <option value="Minivan">Minivan</option>
                <option value="Black Car">Black Car</option>
                <option value="Black SUV">Black SUV</option>
                <option value="Limousine">Limousine</option>
                <option value="Tesla">Tesla</option>
                <option value="Private Van">Private Van</option>
                <option value="Mini Bus">Mini Bus</option>
                <option value="34 Pax Bus">34 Pax Bus</option>
                <option value="56 Pax Bus">56 Pax Bus</option>
              </select>
            </div>
          </div>

          <div class="small-6 medium-2 columns end">
            <div class="status">
              <label class="field-label">Service:</label>
              <select v-model="selectedService" :disabled="sectorLoading || loading">
                <option value="">ALL</option>
                <option value="PAX">PAX</option>
                <option value="DTD">DTD</option>
                <option value="PAX/DTD">PAX/DTD</option>
              </select>
            </div>
          </div>

          <div class="small-6 medium-3 columns end">
            <div class="status">
              <label class="field-label">Assignment:</label>
              <select v-model="selectedAssigned" :disabled="sectorLoading || loading">
                <option value="">ALL</option>
                <option value="1">Assigned</option>
                <option value="0">No assignments</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="small-1 columns end">
        <div class="status search">
          <a :class="{'button cta success':true, 'disabled':sectorLoading || loading}"
            @click="updateTable">
            <i class="fas fa-search"></i>
            Search
          </a>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import datepicker from 'vuejs-datepicker'
import moment from 'moment-timezone'
// import featureToggle from '@/modules/api/featureToggle'
import ApiProviderAvailability from '@/modules/api/provider/availability'

export default {
  name: 'table-filter',
  components: {datepicker},
  data() {
    return {
      maxHour: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      dateError: {
        showMsg: false,
        target: ''
      },
      shiftActive: '',
      dfault: {
        startDate: '',
        endDate: ''
      }
    }
  },
  computed: {
    // loading () { return this.$store.state.provider.availability.loading },
    sectors () { return this.$store.state.provider.sectors.list },
    selectedSector: {
      get: function () { return this.$store.state.provider.availability.sector },
      set: function (value) {
        this.$store.commit('provider/setAvailabilitySector', value)
        // this.updateTable()
      }
    },
    loading () { return this.$store.state.provider.availability.loading },
    sectorLoading () { return this.$store.state.provider.sectors.loading },
    startDate () {
      let datetime = moment(this.$store.state.provider.availability.since, 'X')
      let hourFormat = this.$settings.get('MilitaryTimeBackoffice') === '1' ? 'HH' : 'hh'
      return {
        date: datetime.format('MM/DD/YYYY'),
        hour: datetime.format(hourFormat),
        minute: datetime.format('mm'),
        amPm: datetime.format('A')
      }
    },
    endDate () {
      let datetime = moment(this.$store.state.provider.availability.until, 'X')
      let hourFormat = this.$settings.get('MilitaryTimeBackoffice') === '1' ? 'HH' : 'hh'
      return {
        date: datetime.format('MM/DD/YYYY'),
        hour: datetime.format(hourFormat),
        minute: datetime.format('mm'),
        amPm: datetime.format('A')
      }
    },
    isMilitaryTime () {
      let isMilitaryTime = false
      if (this.$settings.get('MilitaryTimeBackoffice') === '1') {
        isMilitaryTime = this.$settings.get('MilitaryTimeBackoffice') === '1'
      }
      if (isMilitaryTime) {
        this.maxHour = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23]
      }
      return isMilitaryTime
    },
    selectedService: {
      get: function () { return this.$store.state.provider.availability.service },
      set: function (value) { this.$store.commit('provider/setAvailabilityService', value) }
    },
    selectedType: {
      get: function () { return this.$store.state.provider.availability.type },
      set: function (value) { this.$store.commit('provider/setAvailabilityType', value) }
    },
    vehicleType: {
      get () { return this.$store.state.provider.availability.vehicleType },
      set (x) { this.$store.commit('provider/setAvailabilityData', {vehicleType: x}) }
    },
    selectedAssigned: {
      get: function () { return this.$store.state.provider.availability.assignedTrips },
      set: function (value) { this.$store.commit('provider/setAvailabilityAssignedTrips', value) }
    }
  },
  methods: {
    getSectors () {
      let self = this
      self.$store.commit('provider/setSectorLoading', true)
      self.$sdk.get('/sectors', {
        token: self.$store.state.session.sessionToken,
        pageSize: 50
      }, r => {
        if (r.error) {
          return
        }
        let sectors = []
        r.list.forEach(function (sector) {
          sectors.push({
            id: sector.ID,
            name: sector.name
          })
        })
        self.$store.commit('provider/setSectorsList', sectors)
        self.$store.commit('provider/setSectorLoading', false)
      })
    },
    setInitialDate () {
      let timeNow = moment()
      let timeFixed = parseInt(timeNow.format('mm')) % 5
      if (timeFixed > 0) {
        timeNow = timeNow.add(5, 'minutes').subtract(timeFixed, 'minutes')
      }

      // Default date time now until +5 hours
      this.$store.commit('provider/setAvailabilitySince', timeNow.format('X'))
      this.dfault.startDate = timeNow.format('X')
      // this.$store.commit('provider/setAvailabilityUntil', timeNow.add(5, 'hours').format('X'))
      this.$store.commit('provider/setAvailabilityUntil', timeNow.hours(23).minutes(55).format('X'))
      this.dfault.endDate = timeNow.hours(23).minutes(55).format('X')
    },
    leadingZero (n) { return n < 10 ? '0' + n : n },
    calendarFormat (date) { return moment(date).format('MM/DD/YYYY') },
    changeDate (target) {
      let selectedDate = moment(this[target + 'Date'].date)
      let selectedTime = this[target + 'Date'].hour + ':' + this[target + 'Date'].minute
      let timeFormat = 'hh:mm'

      if (!this.isMilitaryTime) {
        selectedTime = selectedTime + this[target + 'Date'].amPm
        timeFormat = 'HH:mmA'
      }
      selectedDate = moment(selectedDate.format('MM/DD/YYYY') + ' ' + selectedTime, `MM/DD/YYYY ${timeFormat}`)

      if (target === 'start') {
        this.$store.commit('provider/setAvailabilitySince', selectedDate.format('X'))
      } else {
        this.$store.commit('provider/setAvailabilityUntil', selectedDate.format('X'))
      }
    },
    updateDate (target) {
      let selectedDate = this[target + 'Date'].date
      let selectedTime = this[target + 'Date'].hour + ':' + this[target + 'Date'].minute
      let timeFormat = 'hh:mm'

      if (!this.isMilitaryTime) {
        selectedTime = selectedTime + this[target + 'Date'].amPm
        timeFormat = 'HH:mmA'
      }
      selectedDate = moment(`${selectedDate} ${selectedTime}`, `MM/DD/YYYY ${timeFormat}`)

      if (target === 'start') {
        this.$store.commit('provider/setAvailabilitySince', selectedDate.format('X'))
      } else {
        this.$store.commit('provider/setAvailabilityUntil', selectedDate.format('X'))
      }
      this.shiftActive = ''
    },
    updateTable () {
      let self = this
      let availability = self.$store.state.provider.availability
      let startDate = moment(availability.since, 'X')
      let endDate = moment(availability.until, 'X')

      self.dateError.showMsg = false
      self.dateError.target = ''
      if (endDate.diff(startDate) <= 0) {
        self.dateError.showMsg = true
        self.dateError.target = 'date'
        if (startDate.format('MM/DD/YYYY') === endDate.format('MM/DD/YYYY')) {
          self.dateError.target = 'time'
        }
        return
      }

      self.$store.commit('provider/resetAvailabilityList')
      self.$store.commit('provider/setAvailabilityIsLoading', true)
      this.$store.commit('provider/setAvailabilityCurrentPage', 1)
      // self.$store.commit('provider/setAvailabilityNoShiftData', {
      //   list: [],
      //   currentPage: 1,
      //   maxList: 0
      // })
      ApiProviderAvailability.getList(self, res => {
        self.$store.commit('provider/setAvailabilityIsLoading', false)
      })
    },
    toggleShift (x) {
      if (this.sectorLoading || this.loading) return
      if (this.shiftActive === x) {
        this.shiftActive = ''
        this.$store.commit('provider/setAvailabilitySince', this.dfault.startDate)
        this.$store.commit('provider/setAvailabilityUntil', this.dfault.endDate)
        return
      }
      this.shiftActive = x
      if (x === 'AM') {
        let startDate = this.startDate.date
        let endDate = this.endDate.date
        this.$store.commit('provider/setAvailabilitySince', moment(`${startDate} 12:00AM`, `MM/DD/YYYY hh:mmA`).format('X'))
        this.$store.commit('provider/setAvailabilityUntil', moment(`${endDate} 11:55AM`, `MM/DD/YYYY hh:mmA`).format('X'))
      }
      if (x === 'PM') {
        let startDate = this.startDate.date
        let endDate = this.endDate.date
        this.$store.commit('provider/setAvailabilitySince', moment(`${startDate} 12:00PM`, `MM/DD/YYYY hh:mmA`).format('X'))
        this.$store.commit('provider/setAvailabilityUntil', moment(`${endDate} 11:55PM`, `MM/DD/YYYY hh:mmA`).format('X'))
      }
    }
  },
  mounted () {
    let self = this
    self.getSectors()
    self.setInitialDate()
    setTimeout(function () {
      self.updateTable()
    }, 150)
  }
}
</script>

<style lang="scss" scoped>
.filter{
  border-bottom:1px solid #bbb;
  background: #fcfcfc;
  margin-bottom: 10px;
  border-radius: 2px;

  .row{ max-width: 100%; }
}

.field-label{
  font-size: 12.5px;
  color: #777;
  line-height: 1;
  display: block;
  margin-bottom: 2px;
  letter-spacing: .4px;
}

select{
  padding: 5px 10px;
  height: 36px;
  outline: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url('/assets/img/dropdown-arrow.png');
  background-repeat: no-repeat;
  background-position: right 50%;

  &[disabled]{ background-color: #ebebe4; }
}

.time {
  select {
    display: inline-block;
    // width: auto;
    width: 32%;
    margin-top: 5px;
  }
}

.date-box{ position: relative; }
.error-box{
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  font-style: italic;
  font-size: 13px;
  background: #ff6115;
  color: #fff;
  padding: 0 15px;
  text-align: center;
  transform-origin: bottom center;
}
.has-error{
  border: 1px solid #f33a27;
}

.status{
  display: inline-block;
  padding: 15px 0 15px 15px;
  width: 100%;

  &.last{
    padding-right: 3px;
    padding-left: 3px;

    button{
      height: 46px;
    }
  }

  &.search {
    padding-top: 30px;
  }
}

.shift-slash{
  margin: 0 5px;
}
.shift-btn{
  border-radius: 5px;
  display: inline-block;
  padding: 5px 10px;
  background: #d7edff;
  border: 1px solid #2181d6;
  font-size: 14px;
  color: #2181d6;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 1px 1px 1px rgba(0, 0, 0, .1);
  flex-grow: 1;
  text-align: center;

  &.active{
    background: #2181d6;
    color: #fff;
  }
  &.disabled{
    background: #eee;
    border-color: #ccc;
    color: #bbb;
    cursor: default;
  }
}

.slide-up-enter-active, .slide-up-leave-active { transition: all .3s ease; }
.slide-up-enter, .slide-up-leave-to {
  transform: rotateX(-90deg);
  opacity: 0;
}

@media only screen and (max-width: 430px) {
  .filter {
    .row {
      padding-top: 25px;

      .small-5 {
        .row {
          padding: 0;
          display: flex;
          .small-4 {
            width: 100%;
            .status {
              padding: 10px;
            }
          }
        }
      }

      .status {
        padding: 10px;
        padding-top: 25px;
        padding-bottom: 0;
      }

      .date-box {
        padding: 0;
        .status {
          padding: 10px;

          .time {
            display: flex;
            gap: 3px;
            select {
              width: 100%;
              height: auto;
            }
          }
        }
      }
    }
  }
  .small-1, .small-2, .small-4, .small-5 {
    width: 100%;
    .small-4 {
      width: 25%;
    }

    .status.search {
      padding: 10px;
      .button {
        width: 100%;
      }
    }
  }

  .end-date {
    /deep/ .vdp-datepicker__calendar {
      right: 1px;
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/ProviderAvailability/TableFilter.vue